// Staff roster (nursing-ops sprint, Epic 7 / E7-FE; redesigned 2026-07-27 into
// a 4-tab shell): Weekly schedule, Roster entries, Leave requests, Nurse
// directory. All roster/nurse data is fetched once here and passed down —
// tabs are pure display, no tab owns its own fetch of shared data.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import {
  getRoster, getWardRoster, updateWardRosterStatus, removeWardRoster, listOf,
  getRosterHours, copyRosterWeek, getMyAssignedWards, getAssignments,
} from "../api/nurseApi";
import useBedCensusData from "../../ipd/hooks/useBedCensusData";
import { usePermissions } from "../../login/PermissionContext";
import { getEffectiveNurseRole } from "../nurseRoleHelper";
import { coveredBedCounts, useCurrentShift } from "../hooks";
import RosterAuthoringForm from "../components/RosterAuthoringForm";
import WeekGrid from "../components/WeekGrid";
import LeaveApprovalsSection from "../components/LeaveApprovalsSection";
import RosterEntriesTab from "../components/RosterEntriesTab";
import NurseDirectoryTab from "../components/NurseDirectoryTab";
import ShiftScheduleTab from "../components/ShiftScheduleTab";
import InchargeManagementTab from "../components/InchargeManagementTab";

export default function StaffRoster() {
  const { userInfo } = usePermissions();
  const roleInfo = useMemo(() => getEffectiveNurseRole(userInfo?.role_permission?.[0]?.role), [userInfo]);
  const [assignedWardData, setAssignedWardData] = useState(null);

  const { beds } = useBedCensusData();
  // A ward is a room category. This used to be a department -> ward map built by
  // grouping beds by their department; beds no longer have one, and a shared ICU
  // is a single ward rather than one per department.
  const wards = useMemo(() => {
    const seen = new Map();
    beds.forEach((b) => {
      if (!b.room_category_code || seen.has(b.room_category_code)) return;
      seen.set(b.room_category_code, b.room_category_name || b.room_category_code);
    });
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [beds]);

  useEffect(() => {
    getMyAssignedWards()
      .then((res) => {
        if (res?.status === "Success") {
          setAssignedWardData(res.data);
        }
      })
      .catch(() => {});
  }, []);

  // For Ward Incharge: strictly scope authoring and grid to their assigned wards.
  // If not assigned to any ward, they cannot see any ward rosters.
  // For Superintendent: full hospital-wide wards.
  const effectiveWards = useMemo(() => {
    if (roleInfo.isSuperintendent || assignedWardData?.is_all_wards) {
      return wards;
    }
    if (roleInfo.isIncharge) {
      if (assignedWardData?.wards?.length) {
        const allowedCodes = new Set(assignedWardData.wards.map((w) => w.ward_code));
        return wards.filter((w) => allowedCodes.has(w.code));
      }
      return []; // Strict empty when Ward Incharge has no assigned ward
    }
    return wards;
  }, [wards, roleInfo.isSuperintendent, roleInfo.isIncharge, assignedWardData]);

  const wardNameByCode = useMemo(() => {
    const m = {};
    beds.forEach((b) => {
      if (b.room_category_code && !(b.room_category_code in m)) m[b.room_category_code] = b.room_category_name || b.room_category_code;
    });
    return m;
  }, [beds]);

  const tabs = useMemo(() => {
    const list = [
      { key: "weekly", label: "Weekly schedule" },
      { key: "entries", label: "Roster entries" },
      { key: "leave", label: "Leave requests" },
      { key: "directory", label: "Nurse directory" },
      { key: "shifts", label: "Shift schedule" },
    ];
    if (roleInfo.isSuperintendent) {
      list.push({ key: "incharges", label: "Ward Incharges" });
    }
    return list;
  }, [roleInfo.isSuperintendent]);

  const [tab, setTab] = useState("weekly");
  // A tab mounts the first time it is opened and stays mounted after. Mounting
  // all of them up front fired ~17 requests at once, past the server's
  // 15-a-second limit, which then refuses the user's every call for 30 minutes.
  const [opened, setOpened] = useState(() => new Set(["weekly"]));
  const openTab = (next) => {
    setTab(next);
    setOpened((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
  };
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const [nurses, setNurses] = useState([]);
  const [nursesLoading, setNursesLoading] = useState(true);
  // Resolves a roster/leave row's nurse_id to a name when the row itself
  // didn't carry one — never let the raw hex id leak into the UI.
  const nurseNameById = useMemo(() => {
    const m = {};
    nurses.forEach((n) => { m[n.nurse_id] = n.name; });
    return m;
  }, [nurses]);
  const [rosterRows, setRosterRows] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  // The roster date of the shift in progress, and its rows — the directory's
  // duty and today's shifts are read from these.
  const { rosterDate, shift, schedule } = useCurrentShift();
  const [todayRoster, setTodayRoster] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [hoursByNurse, setHoursByNurse] = useState({});
  const [copying, setCopying] = useState(false);

  const reloadNurses = useCallback(() => {
    setNursesLoading(true);
    getRoster().then((r) => setNurses(listOf(r))).catch(() => {}).finally(() => setNursesLoading(false));
  }, []);

  const reloadHours = useCallback(() => {
    getRosterHours()
      .then((r) => {
        const map = {};
        listOf(r).forEach((h) => { map[h.nurse_id] = h; });
        setHoursByNurse(map);
      })
      .catch(() => setHoursByNurse({}));
  }, []);

  // Today's rows come in their own call: the unscoped list below stops at
  // 1000 rows in no particular order, so it cannot be relied on to hold them.
  const reloadToday = useCallback(() => {
    getWardRoster({ from_date: rosterDate, to_date: rosterDate })
      .then((r) => setTodayRoster(listOf(r)))
      .catch(() => setTodayRoster([]));
  }, [rosterDate]);

  const reloadRows = useCallback(() => {
    setRosterLoading(true);
    return getWardRoster()
      .then((r) => setRosterRows(listOf(r)))
      .catch(() => setRosterRows([]))
      .finally(() => setRosterLoading(false));
  }, []);

  const reloadRoster = useCallback(() => {
    reloadHours();
    reloadToday();
    return reloadRows();
  }, [reloadHours, reloadToday, reloadRows]);

  useEffect(() => { reloadNurses(); reloadHours(); reloadRows(); }, [reloadNurses, reloadHours, reloadRows]);
  // Separately, so the roster date settling once the facility's schedule
  // arrives refetches today's rows alone rather than every list again.
  useEffect(() => { reloadToday(); }, [reloadToday]);

  // The directory's "Beds this shift" — the same count as the nurse badge on
  // Bed Assignments (rostered wards plus pins in force, occupied beds only), so
  // the two tabs cannot disagree for one nurse (QA NM #5).
  const [pins, setPins] = useState([]);
  useEffect(() => {
    getAssignments().then((r) => setPins(listOf(r))).catch(() => setPins([]));
  }, []);
  const bedCountByNurse = useMemo(
    () => coveredBedCounts(beds, todayRoster, pins, rosterDate, shift),
    [beds, todayRoster, pins, rosterDate, shift]
  );

  const handleCopyWeek = async () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const src = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
    setCopying(true);
    try {
      const res = await copyRosterWeek(src);
      const created = Array.isArray(res?.data) ? res.data.length : 0;
      const skipped = (res?.skipped || []).length;
      const errors = res?.errors || [];
      if (res?.status !== "Success" && created === 0) {
        toast.error(res?.message || "Copy week failed");
      } else if (errors.length > 0) {
        toast.error(`${created} copied, ${skipped} skipped, ${errors.length} rejected — first: ${errors[0]?.nurse ? `${errors[0].nurse} ` : ""}${errors[0]?.roster_date}: ${errors[0]?.error}`);
      } else {
        toast.success(res?.message || `${created} row(s) copied, ${skipped} skipped`);
      }
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Copy week failed");
    } finally {
      setCopying(false);
    }
  };

  const handleStatusChange = async (row, newStatus) => {
    if (newStatus === row.status) return;
    setSavingId(row.id);
    try {
      const res = await updateWardRosterStatus(row.id, newStatus);
      if (res?.status !== "Success") {
        toast.error(res?.message || "Status update failed");
        return;
      }
      toast.success(res?.message || "Roster status updated");
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status update failed");
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove ${row.nurse_name || nurseNameById[row.nurse_id] || row.nurse_id}'s roster row for ${row.roster_date}?`)) return;
    setSavingId(row.id);
    try {
      const res = await removeWardRoster(row.id);
      if (res?.status !== "Success") {
        toast.error(res?.message || "Remove failed");
        return;
      }
      toast.success(res?.message || "Roster row removed");
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Remove failed");
    } finally {
      setSavingId(null);
    }
  };

  const sortedRows = useMemo(
    () => [...rosterRows].sort((a, b) => String(b.roster_date).localeCompare(String(a.roster_date))),
    [rosterRows]
  );

  const closeBulkAssign = () => setBulkAssignOpen(false);
  const handleBulkCreated = () => {
    reloadRoster();
    setBulkAssignOpen(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Staff Roster</Typography>
        <Typography variant="body2" color="text.secondary">Build shift schedules, review leave requests, and see who's on duty this week.</Typography>
      </Box>

      {roleInfo.isIncharge && !roleInfo.isSuperintendent && effectiveWards.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          You are signed in as <strong>Ward Incharge</strong>, but no ward is currently assigned to you.
          Roster schedules and ratio views are strictly restricted to your assigned ward(s). Please contact the Nursing Superintendent for ward assignment.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, next) => openTab(next)}
        sx={{
          mb: 2, minHeight: 0,
          "& .MuiTabs-flexContainer": { gap: 0.5 },
          "& .MuiTab-root": { minHeight: 36, borderRadius: 2, textTransform: "none", fontWeight: 600 },
        }}
      >
        {tabs.map((t) => (<Tab key={t.key} value={t.key} label={t.label} />))}
      </Tabs>

      <Box sx={{ display: tab === "weekly" ? "block" : "none" }}>
        <WeekGrid
          wards={effectiveWards} nurses={nurses} onChanged={reloadRoster}
          onBulkAssign={() => setBulkAssignOpen(true)} onCopyWeek={handleCopyWeek} copying={copying}
        />

        <Dialog open={bulkAssignOpen} onClose={closeBulkAssign} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            Bulk assign shifts
            <IconButton size="small" onClick={closeBulkAssign}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <RosterAuthoringForm wards={effectiveWards} nurses={nurses} onCreated={handleBulkCreated} />
          </DialogContent>
        </Dialog>
      </Box>

      {opened.has("entries") && (
        <Box sx={{ display: tab === "entries" ? "block" : "none" }}>
          <RosterEntriesTab
            rows={sortedRows}
            hoursByNurse={hoursByNurse}
            wardNameByCode={wardNameByCode}
            nurseNameById={nurseNameById}
            savingId={savingId}
            loading={rosterLoading}
            onStatusChange={handleStatusChange}
            onRemove={handleRemove}
          />
        </Box>
      )}

      {opened.has("leave") && (
        <Box sx={{ display: tab === "leave" ? "block" : "none" }}>
          <LeaveApprovalsSection onChanged={reloadRoster} beds={beds} nurseNameById={nurseNameById} />
        </Box>
      )}

      {opened.has("directory") && (
        <Box sx={{ display: tab === "directory" ? "block" : "none" }}>
          <NurseDirectoryTab
            nurses={nurses} loading={nursesLoading} todayRoster={todayRoster}
            shift={shift} schedule={schedule} wardNameByCode={wardNameByCode}
            bedCountByNurse={bedCountByNurse}
          />
        </Box>
      )}

      {opened.has("shifts") && (
        <Box sx={{ display: tab === "shifts" ? "block" : "none" }}>
          <ShiftScheduleTab onChanged={reloadRoster} />
        </Box>
      )}

      {roleInfo.isSuperintendent && opened.has("incharges") && (
        <Box sx={{ display: tab === "incharges" ? "block" : "none" }}>
          <InchargeManagementTab wards={wards} nurses={nurses} onChanged={reloadRoster} />
        </Box>
      )}
    </Box>
  );
}
